import React, { useCallback, useEffect, useState } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import { CurrencyInput } from '../ui/CurrencyInput';
import RichTextEditor from '../ui/RichTextEditor';
import {
  createEvent, fetchEvent, formatDisplayTime, updateEvent, type StoredEvent,
} from '../../services/EventsService';
import { toIsoDate } from '../../utils/eventDates';
import { useRole } from '../../hooks/useRole';
import { useFormSaveEvent, firstMissingField, useClearErrorsOnEdit } from './_shared';
import { useNotifications } from '../NotificationProvider';
import KioskAccessSection from './KioskAccessSection';

interface EventFormPageProps {
  eventId?: string;
  onSave: (saved: StoredEvent) => void;
  onCancel: () => void;
  /** Called with the event once it is loaded, so the page can show its name and archive state. */
  onLoaded?: (event: StoredEvent) => void;
  /** Called when the event could not be loaded. */
  onLoadError?: (error: unknown) => void;
  /** The page's own view of the archive state (it changes when the event is restored). */
  archived?: boolean;
}

const STATUS_OPTIONS = ['upcoming', 'in progress', 'completed', 'cancelled'];

interface FormState {
  title: string; category: string; status: string; date: string; endDate: string;
  startTime: string; endTime: string; timezone: string; location: string; venue: string;
  address: string; city: string; googleMapsLink: string; dresscode: string; theme: string;
  headcount: string; capacity: string; description: string; notes: string;
  price: string; budgetAllocation: string;
}

const EMPTY: FormState = {
  title: '', category: '', status: 'upcoming', date: '', endDate: '', startTime: '', endTime: '', timezone: '',
  location: '', venue: '', address: '', city: '', googleMapsLink: '', dresscode: '', theme: '',
  headcount: '', capacity: '', description: '', notes: '', price: '', budgetAllocation: '',
};

const fromEvent = (e: StoredEvent): FormState => ({
  title: e.title, category: e.category, status: e.status, date: toIsoDate(e.date), endDate: toIsoDate(e.endDate),
  startTime: e.startTime ?? '', endTime: e.endTime ?? '', timezone: e.timezone ?? '', location: e.location,
  venue: e.venue ?? '', address: e.address ?? '', city: e.city ?? '', googleMapsLink: e.googleMapsLink ?? '',
  dresscode: e.dresscode ?? '', theme: e.theme ?? '', headcount: e.headcount === undefined ? '' : String(e.headcount),
  capacity: e.capacity === undefined ? '' : String(e.capacity), description: e.description, notes: e.notes ?? '',
  price: e.price, budgetAllocation: e.budgetAllocation ?? '',
});

const wholeNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
};

export const EventFormPage: React.FC<EventFormPageProps> = ({ eventId, onSave, onCancel, onLoaded, onLoadError, archived: archivedProp }) => {
  const { showError } = useNotifications();
  const { canViewFinancials, canEdit, isAdmin } = useRole();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [archived, setArchived] = useState(false);
  useEffect(() => { if (archivedProp !== undefined) setArchived(archivedProp); }, [archivedProp]);
  // A viewer reads the form; an archived event is read-only until restored.
  const locked = archived || !canEdit;
  const set = (patch: Partial<FormState>) => setForm(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    fetchEvent(eventId)
      .then(event => {
        if (cancelled) return;
        setForm(fromEvent(event));
        setArchived(!!event.archived);
        onLoaded?.(event);
      })
      .catch(err => { if (!cancelled) { if (onLoadError) onLoadError(err); else showError('Could not load this event.'); } });
    return () => { cancelled = true; };
    // onLoaded and showError are stable enough for a one-off load per event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleSave = useCallback(async () => {
    const missing = firstMissingField([{ label: 'Title', value: form.title }]);
    if (missing) { showError(missing); return; }
    if (!canEdit) { showError('You can view this event but not change it.'); return; }
    if (archived) { showError('Restore this event before editing it.'); return; }
    const headcount = wholeNumber(form.headcount);
    const capacity = wholeNumber(form.capacity);
    if (form.headcount.trim() !== '' && headcount === undefined) { showError('Headcount must be a whole number.'); return; }
    if (form.capacity.trim() !== '' && capacity === undefined) { showError('Capacity must be a whole number.'); return; }
    const data: Partial<StoredEvent> & { title: string } = {
      title: form.title.trim(), category: form.category, status: form.status, date: form.date, endDate: form.endDate,
      startTime: form.startTime, endTime: form.endTime, time: form.startTime ? formatDisplayTime(form.startTime) : '',
      timezone: form.timezone, location: form.location, venue: form.venue, address: form.address, city: form.city,
      googleMapsLink: form.googleMapsLink, dresscode: form.dresscode, theme: form.theme,
      description: form.description, notes: form.notes,
      headcount: form.headcount.trim() === '' ? undefined : headcount,
      capacity: form.capacity.trim() === '' ? undefined : capacity,
    };
    // Money is only sent by a person who can see it; the API drops it for anyone else anyway.
    if (canViewFinancials) { data.price = form.price; data.budgetAllocation = form.budgetAllocation; }
    // Both calls wait for the API and throw on failure, so a 400 reaches the
    // shared hook (field errors under their fields) and the form stays open.
    const saved = eventId ? await updateEvent(eventId, data) : await createEvent(data);
    onSave(saved);
  }, [archived, canEdit, canViewFinancials, eventId, form, onSave, showError]);

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the event.' });
  useClearErrorsOnEdit({ ...form }, fieldErrors, {
    startTime: 'start_time', endTime: 'end_time', endDate: 'end_date', googleMapsLink: 'google_maps_link',
    budgetAllocation: 'budget_allocation',
  });

  const statusOptions = [...new Set([...STATUS_OPTIONS, form.status].filter(Boolean))].map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }));

  return (
    <div className="inline-form-page">
      {archived && <p className="form-helper">This event is archived. Restore it to make changes.</p>}
      {!archived && !canEdit && <p className="form-helper">You can view this event but not change it.</p>}
      <FormInput label="Title" type="text" value={form.title} placeholder="e.g. Reception"
        error={fieldErrors.errorFor('title')} onChange={e => set({ title: e.target.value })} autoFocus required disabled={locked} />
      <div className="form-row">
        <FormInput label="Category" type="text" value={form.category} placeholder="e.g. Ceremony"
          error={fieldErrors.errorFor('category')} onChange={e => set({ category: e.target.value })} disabled={locked} />
        <FormSelect label="Status" value={form.status} error={fieldErrors.errorFor('status')}
          onChange={e => set({ status: e.target.value })} options={statusOptions} disabled={locked} />
      </div>
      <div className="form-row">
        <FormInput label="Date" type="date" value={form.date} error={fieldErrors.errorFor('date')}
          onChange={e => set({ date: e.target.value })} disabled={locked} />
        <FormInput label="End date" type="date" value={form.endDate} error={fieldErrors.errorFor('end_date')}
          help="Only for events that run over more than one day" onChange={e => set({ endDate: e.target.value })} disabled={locked} />
      </div>
      <div className="form-row">
        <FormInput label="Starts" type="time" value={form.startTime} error={fieldErrors.errorFor('start_time')}
          onChange={e => set({ startTime: e.target.value })} disabled={locked} />
        <FormInput label="Ends" type="time" value={form.endTime} error={fieldErrors.errorFor('end_time')}
          onChange={e => set({ endTime: e.target.value })} disabled={locked} />
      </div>
      <FormInput label="Time zone" type="text" value={form.timezone} placeholder="e.g. Europe/London"
        error={fieldErrors.errorFor('timezone')} help="Leave empty to use the project's time zone"
        onChange={e => set({ timezone: e.target.value })} disabled={locked} />
      <div className="form-row">
        <FormInput label="Venue" type="text" value={form.venue} error={fieldErrors.errorFor('venue')}
          onChange={e => set({ venue: e.target.value })} disabled={locked} />
        <FormInput label="Location" type="text" value={form.location} error={fieldErrors.errorFor('location')}
          onChange={e => set({ location: e.target.value })} disabled={locked} />
      </div>
      <div className="form-row">
        <FormInput label="Address" type="text" value={form.address} error={fieldErrors.errorFor('address')}
          onChange={e => set({ address: e.target.value })} disabled={locked} />
        <FormInput label="City" type="text" value={form.city} error={fieldErrors.errorFor('city')}
          onChange={e => set({ city: e.target.value })} disabled={locked} />
      </div>
      <FormInput label="Map link" type="url" value={form.googleMapsLink} error={fieldErrors.errorFor('google_maps_link')}
        onChange={e => set({ googleMapsLink: e.target.value })} disabled={locked} />
      <div className="form-row">
        <FormInput label="Planned headcount" type="number" min={0} value={form.headcount} error={fieldErrors.errorFor('headcount')}
          help="Guests you expect. The confirmed count comes from RSVPs." onChange={e => set({ headcount: e.target.value })} disabled={locked} />
        <FormInput label="Capacity" type="number" min={0} value={form.capacity} error={fieldErrors.errorFor('capacity')}
          onChange={e => set({ capacity: e.target.value })} disabled={locked} />
      </div>
      <div className="form-row">
        <FormInput label="Dress code" type="text" value={form.dresscode} error={fieldErrors.errorFor('dresscode')}
          onChange={e => set({ dresscode: e.target.value })} disabled={locked} />
        <FormInput label="Theme" type="text" value={form.theme} error={fieldErrors.errorFor('theme')}
          onChange={e => set({ theme: e.target.value })} disabled={locked} />
      </div>
      {canViewFinancials && (
        <div className="form-row">
          <CurrencyInput label="Price" value={form.price} error={fieldErrors.errorFor('price')}
            onChange={e => set({ price: e.target.value })} disabled={locked} />
          <CurrencyInput label="Budget allocation" value={form.budgetAllocation} error={fieldErrors.errorFor('budget_allocation')}
            onChange={e => set({ budgetAllocation: e.target.value })} disabled={locked} />
        </div>
      )}
      <RichTextEditor label="Description" value={form.description} error={fieldErrors.errorFor('description')}
        onChange={description => set({ description })} rows={3} disabled={locked} />
      <RichTextEditor label="Notes" value={form.notes} error={fieldErrors.errorFor('notes')}
        onChange={notes => set({ notes })} rows={3} disabled={locked} />
      {eventId && isAdmin && <KioskAccessSection eventId={eventId} />}
    </div>
  );
};

export default EventFormPage;
